import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import {
  LngLatBounds,
  MapMouseEvent,
  MapTouchEvent,
  PointLike,
} from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MapRef, ViewState } from 'react-map-gl';
import { useDebounce, useMeasure } from 'react-use';
import { useDeepCompareMemo } from 'use-deep-compare';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import config from '../../config';
import { FeatureEnum } from '../../services/data/impl/cube/cubeSlice';
import { useFeatureFlags } from '../../services/featureFlags/hooks/useFeatureFlags';
import { useTranslation } from '../../services/translation/hooks/useTranslation';
import { BloomfieldMap } from '../commonUI/BloomfieldMap';
import { flexCenter } from '../commonUI/cssClasses';
import { DiagnosticFeature } from '../commonUI/viewMode/DiagnosticFeature';
import { useGetDropdownsData } from '../ranches/hooks/useGetDropdownData';
import { useGetDropdownFilteredData } from '../ranches/hooks/useGetDropdownFilteredData';
import {
  RanchPageViewEnum,
  selectRanchPageView,
  setRanchFocusArea,
  setRanchPageView,
} from '../ranches/ranchesSlice';
import {
  ResetItemsEnum,
  resetSelectFields,
  selectCurrBlockID,
  selectCurrFeature,
  setCurrBlockByID,
  setCurrPointId,
  setCurrRowNum,
  setCurrRowsideId,
  setCurrScanByID,
  setCurrViewDirection,
  setHoverPointId,
  setHoverRowNum,
  setHoverRowsideId,
} from '../select/selectSlice';
import MapAcresDisplay from './features/acreMeasure/MapBlockInfo';
import { BlueberryFeatureFilter } from './features/color-controls/BlueberryFeatureFilter';
import { GeocoderControl } from './features/geocoderControl/GeocoderControl';
import './features/geocoderControl/GeocoderControl.css';
import MapLegend from './features/legend/MapLegend';
import MapButtons from './features/mapButtons/MapButtons';
import useCheckLayers from './hooks/useCheckLayers';
import { useFindLatestScans } from './hooks/useFindLatestScans';
import BlockNameLayer from './layers/BlockNameLayer';
import BlockOutlineLayer from './layers/BlockOutlineLayer';
import MarkerLayer from './layers/MarkerLayer';
import PointDataLayers from './layers/PointDataLayers';
import MapControls from './MapControls';
import {
  LayerEnum,
  MapZoomModeEnum,
  MAX_ZOOM,
  selectBlockButtonWasClicked,
  selectLayerVisibility,
  selectViewBounds,
  selectZoomMode,
  setBlockViewBounds,
} from './mapSlice';
import { isPointProperties } from './mapUtils';
import MarkerPopup from './MarkerPopup';
import PointInfoPopup from './PointInfoPopup';
import { getBoundsFromBlocks } from './utils/getBoundsFromBlocks';

const mapboxToken = config.mapbox.apiKey;

const showMapLayerControls = false;

export interface PointProperties {
  point_id: string;
  point_location: string;
  rowside_id: string;
  rowside_num: number;
  view_direction: string;
  color: string;
  hueValue: number | undefined;
  flowers?: number | undefined;
}

const Mapbox: React.FC = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const mapRef = useRef<MapRef | null>(null);
  const [sizeRef, { width, height }] = useMeasure();
  const blockButtonWasClicked = useAppSelector(selectBlockButtonWasClicked);
  const layerVisibility = useAppSelector(selectLayerVisibility);
  const ranchPageView = useAppSelector(selectRanchPageView);
  const currBlockID = useAppSelector(selectCurrBlockID);
  const currFeature = useAppSelector(selectCurrFeature);
  const zoomMode = useAppSelector(selectZoomMode);
  const viewBounds = useAppSelector(selectViewBounds);
  const { language } = useTranslation();
  const { t } = useTranslation();

  const { Diagnostics, MeasureTool, GeocoderTool } = useFeatureFlags();

  const layerExistenceDict = useCheckLayers(mapRef.current);
  const [currPointHoverInfo, setCurrPointHoverInfo] =
    useState<null | PointProperties>(null);

  const { customers, ranches, blocks } = useGetDropdownsData();

  const { blocksFiltered, scansFilteredByBlock } = useGetDropdownFilteredData({
    customers,
    ranches,
    blocks,
  });

  const latestScans = useFindLatestScans(blocksFiltered, scansFilteredByBlock);

  // The layer existence dictionary constantly tracks which layers are available and this code turns the interactive layers on/off based on if they exist
  const interactiveLayerIds: string[] = useDeepCompareMemo(() => {
    const layers: string[] = [];
    if (layerExistenceDict[LayerEnum.blockOutlines]) {
      layers.push(LayerEnum.blockOutlines);
      layers.push(`${LayerEnum.blockOutlines}-line`);
    }

    if (layerExistenceDict[LayerEnum.heatmap]) {
      layers.push(LayerEnum.heatmap);
    }

    return layers;
  }, [layerExistenceDict]);

  const onHover = useCallback(
    (event: MapMouseEvent | MapTouchEvent) => {
      // At a minimum, don't query on hover until blocks are loaded
      if (mapRef?.current?.getLayer(`${LayerEnum.blockOutlines}-line`)) {
        // Check if features exist.  Multiple items can hovered at once so we need to check for multiples and then pull first item
        const featureData = mapRef?.current?.queryRenderedFeatures(
          event.point,
          {
            layers: interactiveLayerIds.filter(
              (el) =>
                ![
                  `${LayerEnum.blockOutlines}-line`,
                  `${LayerEnum.blockOutlines}`,
                ].includes(el)
            ),
          }
        );

        if (!featureData?.length) {
          setCurrPointHoverInfo(null);
          return;
        }

        const topLevelFeature =
          featureData?.length && featureData[featureData?.length - 1];

        // Check if feature has properties
        if (topLevelFeature && 'properties' in topLevelFeature) {
          if (topLevelFeature.properties) {
            // Check that all PointProperties fields exist and update the hover info if they do
            if (isPointProperties(topLevelFeature.properties)) {
              setCurrPointHoverInfo(topLevelFeature.properties);
            }
          }
        }
      }
    },
    [interactiveLayerIds]
  );

  // Debounce mouse hovers to avoid spamming redux state which causes lag.
  useDebounce(
    () => {
      if (currPointHoverInfo) {
        // dispatch(setCurrRowNum(currPointHoverInfo.rowside_num));
        dispatch(setHoverPointId(currPointHoverInfo.point_id));
        dispatch(setHoverRowNum(currPointHoverInfo.rowside_num));
        dispatch(setHoverRowsideId(currPointHoverInfo.rowside_id));
      } else {
        // Reset hover info in redux state when hoverInfo is null
        dispatch(
          resetSelectFields([
            ResetItemsEnum.HoverPointId,
            ResetItemsEnum.HoverRowNum,
            ResetItemsEnum.HoverRowsideId,
          ])
        );
      }
    },
    20, // debounce time in ms
    [currPointHoverInfo, dispatch]
  );

  const onFeatureClick = useCallback(
    // Handle clicks on map features
    (event: MapMouseEvent | MapTouchEvent) => {
      // If information is present about the point currently being hovered over, dispatch this information to redux
      if (currPointHoverInfo) {
        const { rowside_num, view_direction, rowside_id, point_id } =
          currPointHoverInfo;
        dispatch(setCurrRowNum(rowside_num));
        dispatch(setCurrViewDirection(view_direction));
        dispatch(setCurrRowsideId(rowside_id));
        dispatch(setCurrPointId(point_id));
      }

      // Calculate a bounding box around the point where the click event occurred
      const bbox: [PointLike, PointLike] = [
        [event.point.x - 5, event.point.y - 5],
        [event.point.x + 5, event.point.y + 5],
      ];

      // If currBlockID doesn't exist, try to identify a block feature that was clicked
      if (!currBlockID) {
        const clickedBlock = mapRef?.current?.queryRenderedFeatures(bbox, {
          layers: [`${LayerEnum.blockOutlines}`],
        });

        // If a block feature was clicked, update redux state with the clicked block's ID and other related info
        if (clickedBlock?.length) {
          const blockOutlineFeature = clickedBlock[clickedBlock.length - 1];
          const blockId = blockOutlineFeature?.properties?.blockID;
          dispatch(setCurrBlockByID(blockId));
          dispatch(
            setCurrScanByID(blockOutlineFeature?.properties?.scanID || '')
          );
          dispatch(setRanchFocusArea(null));
          dispatch(setRanchPageView(RanchPageViewEnum.blockDetail));
        }
      }
    },
    [currPointHoverInfo, currBlockID, mapRef, dispatch]
  );

  // update the block boundary viewBounds whenever blocksFiltered changes
  const blockViewBounds = useMemo(() => {
    return getBoundsFromBlocks(blocksFiltered);
  }, [blocksFiltered]);

  useEffect(() => {
    if (blockViewBounds) {
      dispatch(setBlockViewBounds(blockViewBounds));
    }
  }, [blockViewBounds, dispatch]);

  // Center the map on the bounds of the currently selected blocks
  const centerMapOnBounds = useCallback(
    (bounds: LngLatBounds) => {
      if (mapRef.current) {
        mapRef.current.fitBounds(bounds, {
          padding: 20,
        });
      }
    },
    [mapRef]
  );

  const zoomMapWithFallback = useCallback(
    (fallbackZoomMode: MapZoomModeEnum) => {
      if (viewBounds[zoomMode]) {
        centerMapOnBounds(viewBounds[zoomMode] as LngLatBounds);
      } else if (viewBounds[fallbackZoomMode]) {
        centerMapOnBounds(
          viewBounds[MapZoomModeEnum.blockLevel] as LngLatBounds
        );
      }
    },
    [viewBounds, zoomMode, centerMapOnBounds]
  );

  // Center the map when the list of blocks changes, a block button was clicked, or width/height changes
  useEffect(() => {
    if (zoomMode !== MapZoomModeEnum.manual) {
      zoomMapWithFallback(MapZoomModeEnum.blockLevel);
    }
  }, [viewBounds, zoomMode, blockButtonWasClicked, zoomMapWithFallback]);

  // If width or height changes, force mapbox to resize (sometimes it wouldn't resize when navbar was open/closed)
  useDebounce(
    () => {
      if (
        zoomMode !== MapZoomModeEnum.manual &&
        mapRef.current &&
        width &&
        height
      ) {
        mapRef.current.resize();
        setTimeout(() => {
          zoomMapWithFallback(MapZoomModeEnum.blockLevel);
        }, 100);
      }
    },
    100,
    [mapRef, width, height]
  );

  // Set initial view state as zoomed out world view
  let initialViewState: Partial<ViewState> & {
    bounds?: mapboxgl.LngLatBoundsLike | undefined;
    fitBoundsOptions?: mapboxgl.FitBoundsOptions | undefined;
  } = {
    longitude: -98.5795, // center of USA
    latitude: 39.8283,
    zoom: 1, // 1 is world view
  };

  // If there are blocks to show, set initial view state to bounds of blocks
  if (viewBounds[zoomMode] || viewBounds[MapZoomModeEnum.blockLevel]) {
    initialViewState = {
      bounds:
        viewBounds[zoomMode] ||
        viewBounds[MapZoomModeEnum.blockLevel] ||
        undefined,
    };
  }

  return (
    <Box
      ref={sizeRef}
      sx={{
        width: '100%',
        maxWidth: '100%',
        minWidth: '10rem',
        height: '100%',
        ...flexCenter,
        position: 'relative',
      }}
    >
      <BloomfieldMap
        id='map'
        key={'map'}
        ref={mapRef}
        reuseMaps
        trackResize
        initialViewState={initialViewState}
        maxZoom={MAX_ZOOM - 1} // Point vanish if zoom level in point layer is higher than max zoom level in map so we -1
        style={{
          width: '100%',
          height: '100%',
          borderBottomLeftRadius: theme.borderRadius.primary,
          borderBottomRightRadius: theme.borderRadius.primary,
        }}
        onClick={onFeatureClick}
        onMouseMove={onHover}
        interactiveLayerIds={interactiveLayerIds}
      >
        <BlockOutlineLayer
          mapRef={mapRef.current}
          layerExistenceDict={layerExistenceDict}
          blocks={blocksFiltered}
          latestScans={latestScans}
        />
        <BlockNameLayer
          mapRef={mapRef.current}
          layerExistenceDict={layerExistenceDict}
          blocks={blocksFiltered}
          latestScans={latestScans}
        />
        <PointDataLayers
          mapRef={mapRef.current}
          layerExistenceDict={layerExistenceDict}
        />
        {layerVisibility[LayerEnum.markers] && <MarkerLayer />}
        {currPointHoverInfo && (
          <PointInfoPopup pointProperties={currPointHoverInfo} />
        )}
        <MarkerPopup />
        {GeocoderTool && (
          <GeocoderControl
            mapboxAccessToken={`${mapboxToken}`}
            position='top-right'
            language={language}
            placeholder={t('Find address or coordinates')}
          />
        )}
      </BloomfieldMap>
      <MapButtons />
      {currFeature === FeatureEnum.blueberry &&
        ranchPageView === RanchPageViewEnum.blockDetail && (
          <BlueberryFeatureFilter
            position={Diagnostics || MeasureTool ? 'top-left' : 'bottom-left'}
          />
        )}
      {showMapLayerControls && (
        <DiagnosticFeature>
          <MapControls />
        </DiagnosticFeature>
      )}
      <MapLegend height={height} />
      <MapAcresDisplay />
    </Box>
  );
};

export default Mapbox;
